import { auth } from '@/auth'
import { clearSession } from '@/auth/session-storage'
import { useSessionStore } from '@/stores/session'

/**
 * 会话续期与过期跳转（业主 2026-10-10：「到期了就跳转去登录，为啥不跳转登录返回 401，用户看到都蒙圈了」）。
 * Purpose: access token 只有 10 分钟；每次调 API 前若临近过期（≤60 s）就用 refresh token 静默换新，
 *   续不上（无 refresh token / 已吊销 / 会话超时）或服务端仍回 401 时，直接跳 DstMax 登录，登录后回到当前页面——不再把 401 摆给用户。
 * Invariants:
 *   - 同一标签页同时只发起一次刷新（单飞）：refresh token 是轮换的，并发重复兑换会被 DstMax 判为重放而吊销整条链。
 *   - 没有 refresh token 的会话（e2e 直接注入 access token）不走刷新，原样使用当前令牌。
 *   - 跳转只触发一次；跳转前清本标签页会话，避免回退时恢复已过期的会话。
 * Authorization: 纯客户端可见性；服务端始终自行校验签名与有效期，这里不授予任何权限。
 * Failure: 刷新失败 → null → 调用方按 401 处理 → 跳登录。
 * Verification: 预览真栈手工——登录后等 access token 过期再提交单据，请求照常成功（静默续期）；吊销 refresh token 后提交 → 跳登录页。
 */
let inflight: Promise<string | null> | null = null
let redirecting = false

/** 返回可用的 access token；临近过期时静默续期并同步到会话 store。 */
export async function freshAccessToken(): Promise<string | null> {
  const session = useSessionStore()
  if (!sessionStorage.getItem('dstbfs.refresh_token')) return session.accessToken
  inflight ??= auth.getAccessToken().finally(() => { inflight = null })
  const token = await inflight
  if (token && token !== session.accessToken) session.accessToken = token
  return token
}

/** 会话已失效：清本地会话并跳 DstMax 登录，登录后回到当前页面。 */
export function redirectToSignIn(): void {
  if (redirecting) return
  redirecting = true
  clearSession()
  sessionStorage.removeItem('dstbfs.refresh_token')
  const returnTo = window.location.pathname + window.location.search
  void auth.beginLogin({ returnTo }).then((url) => window.location.assign(url), () => { redirecting = false; window.location.assign('/') })
}
